/* eslint-disable react-refresh/only-export-components -- provider + useToast hook co-locate by design. */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

export type ToastTone = "success" | "error" | "warn" | "info";

export interface ToastInput {
  tone?: ToastTone;
  title?: ReactNode;
  message: ReactNode;
  /** Auto-dismiss after ms; 0 = sticky (caller must dismiss). Default 5000. */
  durationMs?: number;
}

interface ToastRecord extends Required<Pick<ToastInput, "message">> {
  id: string;
  tone: ToastTone;
  title?: ReactNode;
  durationMs: number;
}

interface ToastApi {
  toast: (input: ToastInput) => string;
  success: (message: ReactNode, title?: ReactNode) => string;
  error: (message: ReactNode, title?: ReactNode) => string;
  warn: (message: ReactNode, title?: ReactNode) => string;
  info: (message: ReactNode, title?: ReactNode) => string;
  dismiss: (id: string) => void;
}

const TONE_GLYPH: Record<ToastTone, string> = {
  success: "✓",
  error: "◆",
  warn: "▲",
  info: "ⓘ",
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * ToastProvider — mounts the polite live-region and exposes useToast(). Errors are assertive
 * (role="alert"); others are polite (role="status"). Clinical glyphs reinforce tone (never color
 * alone). Wrap the app once near the root.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput): string => {
      const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const rec: ToastRecord = {
        id,
        tone: input.tone ?? "info",
        message: input.message,
        ...(input.title !== undefined ? { title: input.title } : {}),
        durationMs: input.durationMs ?? 5000,
      };
      setToasts((list) => [...list, rec]);
      if (rec.durationMs > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), rec.durationMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (message, title) => push({ tone: "success", message, ...(title !== undefined ? { title } : {}) }),
      error: (message, title) => push({ tone: "error", message, durationMs: 8000, ...(title !== undefined ? { title } : {}) }),
      warn: (message, title) => push({ tone: "warn", message, ...(title !== undefined ? { title } : {}) }),
      info: (message, title) => push({ tone: "info", message, ...(title !== undefined ? { title } : {}) }),
      dismiss,
    }),
    [push, dismiss],
  );

  // WCAG 4.1.3 — two PERSISTENT live regions mounted from boot (a region must pre-exist for AT to
  // announce content injected into it). Errors are routed to the assertive region (role="alert");
  // everything else to the polite region (role="status"). The toast nodes carry NO role of their
  // own — the region owns the live semantics, so there's no polite/assertive conflict.
  const errors = toasts.filter((t) => t.tone === "error");
  const polite = toasts.filter((t) => t.tone !== "error");

  const renderToast = (t: ToastRecord) => (
    <div key={t.id} className={cx("nv-toast", `nv-toast--${t.tone}`)}>
      <span className="nv-toast__glyph" aria-hidden style={glyphColor(t.tone)}>
        {TONE_GLYPH[t.tone]}
      </span>
      <div className="nv-toast__body">
        {t.title && <div className="nv-toast__title">{t.title}</div>}
        <div>{t.message}</div>
      </div>
      <button
        type="button"
        className="nv-iconbtn"
        aria-label="Fermer la notification"
        onClick={() => dismiss(t.id)}
      >
        <span aria-hidden>×</span>
      </button>
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <>
          <div
            className="nv-toast-region"
            role="status"
            aria-live="polite"
            aria-relevant="additions"
          >
            {polite.map(renderToast)}
          </div>
          <div
            className="nv-toast-region nv-toast-region--assertive"
            role="alert"
            aria-live="assertive"
            aria-relevant="additions"
          >
            {errors.map(renderToast)}
          </div>
        </>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function glyphColor(tone: ToastTone): { color: string } {
  const map: Record<ToastTone, string> = {
    success: "var(--nv-success)",
    error: "var(--nv-danger)",
    warn: "var(--nv-warn)",
    info: "var(--nv-info)",
  };
  return { color: map[tone] };
}

/** useToast — the hook every feature uses to surface async outcomes. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}
